import type React from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'

import { type PromotionPlacement } from '@onr/schema'
import { Link } from '@tanstack/react-router'
import { Lock } from 'lucide-react'

import { Checkbox } from '@/components/atoms/Checkbox'

const GRID_PLACEMENTS: ReadonlyArray<PromotionPlacement> = [
  'email',
  'milestone',
  'hero',
  'volunteerProfile',
  'volunteerInfo',
  'clientInfo',
]

export interface RegionPlacementConfiguration {
  _id: string
  name: string
  promotionPlacements: PromotionPlacement[]
  lockedPlacements: PromotionPlacement[]
}

export interface PromotionPlacementRowFormProps {
  region: RegionPlacementConfiguration
  onPlacementChange: (regionId: string, placement: PromotionPlacement, inPlan: boolean) => Promise<void> | void
}

type PendingPlacements = Partial<Record<PromotionPlacement, boolean>>

const withoutPlacement = (pending: PendingPlacements, placement: PromotionPlacement) =>
  Object.fromEntries(Object.entries(pending).filter(([key]) => key !== placement)) as PendingPlacements

const withoutSettled = (pending: PendingPlacements, plan: PromotionPlacement[]) =>
  Object.fromEntries(
    Object.entries(pending).filter(([placement, inPlan]) => inPlan !== plan.includes(placement as PromotionPlacement))
  ) as PendingPlacements

export const PromotionPlacementRowForm: React.FC<PromotionPlacementRowFormProps> = ({ region, onPlacementChange }) => {
  const intl = useIntl()
  const [pending, setPending] = useState<PendingPlacements>({})
  const queue = useRef<Promise<void>>(Promise.resolve())

  useEffect(() => {
    setPending((current) => withoutSettled(current, region.promotionPlacements))
  }, [region.promotionPlacements])

  const change = useCallback(
    (placement: PromotionPlacement, inPlan: boolean) => {
      const send = async () => {
        try {
          await onPlacementChange(region._id, placement, inPlan)
        } catch {
          setPending((current) => (current[placement] === inPlan ? withoutPlacement(current, placement) : current))
        }
      }
      setPending((current) => ({ ...current, [placement]: inPlan }))
      queue.current = queue.current.then(send)
    },
    [onPlacementChange, region._id]
  )

  return (
    <div className="grid h-12 grid-cols-[1fr_repeat(6,120px)] grid-rows-subgrid">
      <div className="flex items-center px-4 py-2">
        <Link
          from="/promotions/$initiativeId/placements"
          to="/promotions/$initiativeId/$regionId"
          params={{ regionId: region._id }}
          target="_blank">
          <p className="text-sm text-gray-900">{region.name}</p>
        </Link>
      </div>
      {GRID_PLACEMENTS.map((placement) => {
        const inPlan = region.promotionPlacements.includes(placement)
        return (
          <div key={placement} className="flex h-12 w-[120px] items-center justify-center gap-1.5 p-4 text-center">
            <Checkbox
              size="sm"
              name={placement}
              aria-label={`${region.name} · ${intl.formatMessage({ id: `promotions.form.placements.${placement}` })}`}
              checked={pending[placement] ?? inPlan}
              onCheckedChange={(checked) => {
                change(placement, checked === true)
              }}
            />
            {inPlan && region.lockedPlacements.includes(placement) && (
              <Lock
                className="size-3 shrink-0 text-gray-600"
                strokeWidth={2.6}
                role="img"
                aria-label={intl.formatMessage({ id: 'promotions.shop.locked' })}
              />
            )}
          </div>
        )
      })}
    </div>
  )
}
